import React, { useState } from "react";
import { Calendar, MapPin, Users, Eye, Paperclip, Pencil, Trash2, Download, Fingerprint, Clock, UserSquare2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { useVault } from "@/context/VaultContext";
import { decryptBytes } from "@/lib/vaultCrypto";
import { categoryOf, formatIncidentDate, formatRecorded, formatBytes } from "@/lib/vaultMeta";
import { toast } from "sonner";

const People = ({ icon: Icon, label, people }) =>
  people?.length > 0 && (
    <div className="flex items-start gap-2 text-sm text-slate-600">
      <Icon size={15} className="text-slate-400 mt-0.5 shrink-0" />
      <span><span className="text-slate-400">{label}:</span> {people.map((p) => [p.name, p.title].filter(Boolean).join(", ")).join(" · ")}</span>
    </div>
  );

export default function VaultEntryCard({ entry, onEdit }) {
  const { vaultKey, loadEntries } = useVault();
  const [busy, setBusy] = useState("");
  const d = entry.data;
  const cat = categoryOf(d?.category);

  const openAttachment = async (a) => {
    setBusy(a.id);
    try {
      const res = await api.get(`/vault/attachments/${a.id}`, { responseType: "arraybuffer" });
      const plain = await decryptBytes(vaultKey, res.data);
      const url = URL.createObjectURL(new Blob([plain], { type: a.meta.type || "application/octet-stream" }));
      const link = document.createElement("a");
      link.href = url;
      if (a.meta.type?.startsWith("image/") || a.meta.type === "application/pdf") link.target = "_blank"; else link.download = a.meta.name;
      link.rel = "noopener";
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not open attachment");
    } finally {
      setBusy("");
    }
  };

  const removeAttachment = async (a) => {
    await api.delete(`/vault/attachments/${a.id}`);
    await loadEntries();
    toast.success("Attachment removed");
  };

  const remove = async () => {
    await api.delete(`/vault/entries/${entry.id}`);
    await loadEntries();
    toast.success("Entry deleted");
  };

  if (entry.corrupt) {
    return <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-700" data-testid={`vault-entry-corrupt-${entry.id}`}>This entry could not be decrypted with the current key.</div>;
  }

  return (
    <article data-testid={`vault-entry-${entry.id}`} className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 hover:border-slate-300 transition-colors">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <span className={`text-[11px] font-mono uppercase tracking-wider border rounded-full px-2 py-0.5 ${cat.cls}`} data-testid="vault-entry-category">{cat.label}</span>
            {d.supervisor_name && <span className="text-[11px] font-mono text-slate-500 border border-slate-200 rounded-full px-2 py-0.5 flex items-center gap-1"><UserSquare2 size={11} /> {d.supervisor_name}</span>}
          </div>
          <h3 className="font-display text-xl text-slate-900 leading-tight" style={{ fontWeight: 700 }} data-testid="vault-entry-title-text">{d.title || "Untitled entry"}</h3>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-sm text-slate-500">
            <span className="flex items-center gap-1.5"><Calendar size={14} /> {formatIncidentDate(d.incident_date)}{d.incident_time ? ` · ${d.incident_time}` : ""}</span>
            {d.location && <span className="flex items-center gap-1.5"><MapPin size={14} /> {d.location}</span>}
          </div>
        </div>
        <div className="flex gap-1">
          <Button data-testid={`vault-edit-${entry.id}`} variant="ghost" size="icon" onClick={() => onEdit(entry)} className="text-slate-500 hover:text-emerald-700" title="Amend"><Pencil size={16} /></Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid={`vault-delete-${entry.id}`} variant="ghost" size="icon" className="text-slate-500 hover:text-rose-600" title="Delete"><Trash2 size={16} /></Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
                <AlertDialogDescription>This permanently removes the entry and its attachments from your vault. Consider exporting first.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep it</AlertDialogCancel>
                <AlertDialogAction data-testid={`vault-delete-confirm-${entry.id}`} onClick={remove} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <p className="text-slate-700 mt-4 whitespace-pre-wrap leading-relaxed" data-testid="vault-entry-what-text">{d.what_happened}</p>

      <div className="mt-4 space-y-1.5">
        <People icon={Users} label="Involved" people={d.individuals} />
        <People icon={Eye} label="Witnesses" people={d.witnesses} />
      </div>

      {entry.attachments?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="vault-entry-attachments">
          {entry.attachments.map((a) => (
            <div key={a.id} className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg pl-3 pr-1 py-1 text-sm">
              <button type="button" data-testid={`vault-attachment-open-${a.id}`} onClick={() => openAttachment(a)} disabled={busy === a.id} className="flex items-center gap-1.5 text-slate-700 hover:text-emerald-700">
                <Paperclip size={13} /> {a.meta.name} <span className="text-slate-400">{formatBytes(a.meta.size || a.size)}</span> {busy === a.id ? <span className="text-xs">decrypting…</span> : <Download size={13} className="text-slate-400" />}
              </button>
              <button type="button" data-testid={`vault-attachment-delete-${a.id}`} onClick={() => removeAttachment(a)} className="p-1 text-slate-400 hover:text-rose-600" title="Remove attachment"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-mono text-slate-400">
        <span className="flex items-center gap-1"><Clock size={11} /> Recorded {formatRecorded(entry.recorded_at)}</span>
        {entry.revision > 1 && <span>Amended {formatRecorded(entry.updated_at)} · rev {entry.revision}</span>}
        <span className="flex items-center gap-1" title="SHA-256 of the encrypted entry, computed by the server when saved"><Fingerprint size={11} /> {entry.content_hash.slice(0, 16)}</span>
      </div>
    </article>
  );
}
